import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';

export const FUND_STATUSES = ['scouting', 'open', 'submitted', 'active', 'reporting', 'closed', 'cancelled'];
export const COUNTRIES = ['Italy', 'Morocco', 'Other'];
export const blankFund = {
  name: '', body_id: '', own_company_id: '', program: '', reference_code: '', country: 'Italy',
  total_budget: '', open_date: '', close_date: '', status: 'scouting', description: '',
};

/** Create/edit a fund (bando). Funding bodies come from the categories taxonomy. */
export default function FundForm({ initial, isAdmin = false, onClose, onSaved, onDeleted }) {
  const confirm = useConfirm();
  const toast = useToast();
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...blankFund, ...initial });
  const [baseline] = useState(() => JSON.stringify({ ...blankFund, ...initial }));
  const [bodies, setBodies] = useState([]);
  const [ownCompanies, setOwnCompanies] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get('/categories?domain=funding_body').then((r) => setBodies(r.data)).catch(() => {});
    api.get('/companies?type=own').then((r) => setOwnCompanies(r.data)).catch(() => {});
  }, []);

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/funds', form);
      else await api.put(`/funds/${initial.id}`, form);
      toast(isNew ? t('Fund created') : t('Fund saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete fund'), message: t('Delete this fund and all client enrollments, milestones and documents under it?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/funds/${initial.id}`);
    toast(t('Fund deleted'));
    (onDeleted || onSaved)();
  }

  return (
    <Modal title={isNew ? t('New fund') : t('Edit fund')} onClose={onClose}
      dirty={JSON.stringify(form) !== baseline}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Name')} error={errors.name}><input value={form.name} onChange={set('name')} /></Field>

      <div className="grid-3">
        <Field label={t('Funding body')}>
          <select value={form.body_id || ''} onChange={set('body_id')}>
            <option value="">—</option>
            {bodies.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{FUND_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
        <Field label={t('Country')}><select value={form.country} onChange={set('country')}>{COUNTRIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}</select></Field>
      </div>

      <Field label={t('Our company (entity handling this fund)')}>
        <select value={form.own_company_id || ''} onChange={set('own_company_id')}>
          <option value="">—</option>
          {ownCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>

      <div className="grid-2">
        <Field label={t('Programme / measure')}><input value={form.program || ''} onChange={set('program')} /></Field>
        <Field label={t('Reference code')}><input value={form.reference_code || ''} onChange={set('reference_code')} /></Field>
      </div>

      <div className="grid-3">
        <Field label={t('Total budget (€)')}><input type="number" step="0.01" value={form.total_budget ?? ''} onChange={set('total_budget')} /></Field>
        <Field label={t('Opens')} error={errors.open_date}><input type="date" value={form.open_date || ''} onChange={set('open_date')} /></Field>
        <Field label={t('Closes')} error={errors.close_date}><input type="date" value={form.close_date || ''} onChange={set('close_date')} /></Field>
      </div>

      <Field label={t('Description')}><textarea rows={3} value={form.description || ''} onChange={set('description')} /></Field>
    </Modal>
  );
}
